import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../api/client'
import {
  useCostCenterCoverage,
  useLatestQcRun,
  usePeriods,
  useQcFindings,
  useQcRules,
  useRunQc,
  useSuppressFinding,
} from '../api/hooks'
import type { QcFinding, QcSeverity } from '../api/types'
import { formatPercent } from '../components/Badges'
import { formatDateTime } from '../components/formatTime'

/**
 * Screen 7 — QC Dashboard (§10).
 *
 * The daily driver of the application and its default landing page. Four cards, findings
 * grouped by rule, every Warning and Info suppressible inline with a mandatory reason,
 * and a Cost Center Coverage tab where §7.4's findings become actionable — the tree view
 * makes an overlooked sibling obvious in a way a findings list does not.
 */

const SEVERITY: Record<QcSeverity, { bg: string; fg: string }> = {
  Error: { bg: 'var(--color-error-wash)', fg: 'var(--color-error)' },
  Warning: { bg: 'var(--color-warn-wash)', fg: 'var(--color-warn)' },
  Info: { bg: 'var(--color-accent-wash)', fg: 'var(--color-accent)' },
}

type Tab = 'findings' | 'coverage'

export function QcDashboard() {
  const { data: periods = [] } = usePeriods()
  const [periodId, setPeriodId] = useState<number | null>(null)
  const [tab, setTab] = useState<Tab>('findings')
  const [severityFilter, setSeverityFilter] = useState<QcSeverity | 'Suppressed' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const effectivePeriodId = periodId ?? periods[0]?.periodId ?? null

  const { data: qcRun } = useLatestQcRun(effectivePeriodId)
  const showSuppressed = severityFilter === 'Suppressed'
  const { data: findings = [] } = useQcFindings(qcRun?.qcRunId ?? null, showSuppressed)
  const { data: rules = [] } = useQcRules()

  const runQc = useRunQc()

  const ruleByCode = useMemo(
    () => new Map(rules.map((r) => [r.ruleCode, r])),
    [rules],
  )

  const visible = useMemo(() => {
    if (showSuppressed) return findings.filter((f) => f.isSuppressed)
    if (severityFilter === null) return findings
    return findings.filter((f) => f.severity === severityFilter)
  }, [findings, severityFilter, showSuppressed])

  const grouped = useMemo(() => {
    const map = new Map<string, QcFinding[]>()
    for (const f of visible) {
      const list = map.get(f.ruleCode)
      if (list) list.push(f)
      else map.set(f.ruleCode, [f])
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [visible])

  return (
    <div className="flex h-full flex-col">
      <div
        className="flex flex-wrap items-center gap-3 border-b px-4 py-2"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
      >
        <h1 className="text-[14px] font-semibold">Quality Control</h1>

        <select
          className="input w-40"
          value={effectivePeriodId ?? ''}
          onChange={(e) => setPeriodId(Number(e.target.value))}
        >
          {periods.map((p) => (
            <option key={p.periodId} value={p.periodId}>
              {p.periodCode} ({p.status})
            </option>
          ))}
        </select>

        <button
          className="btn btn-primary"
          disabled={runQc.isPending || effectivePeriodId === null}
          onClick={() => {
            setError(null)
            runQc.mutate(effectivePeriodId!, {
              onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
            })
          }}
        >
          {runQc.isPending ? 'Running…' : 'Run QC'}
        </button>

        {qcRun && (
          <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
            last run {formatDateTime(qcRun.startedUtc)} by {qcRun.initiatedBy}
          </span>
        )}

        <div className="ml-auto flex gap-0.5">
          {(['findings', 'coverage'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="rounded px-2 py-1 text-[12px] capitalize"
              style={{
                background: tab === t ? 'var(--color-accent)' : 'transparent',
                color: tab === t ? '#fff' : 'var(--color-muted)',
                border: `1px solid ${tab === t ? 'var(--color-accent)' : 'var(--color-rule)'}`,
              }}
            >
              {t === 'coverage' ? 'Cost Center Coverage' : 'Findings'}
            </button>
          ))}
        </div>
      </div>

      {/*
          The findings below predate somebody's edit, and say so.

          NOT resolved automatically, deliberately. A finding is a stored record of what a
          run reported, with suppressions attached to individual findings — amending it
          after the fact would mean this product quietly rewriting its own history, which
          is the one thing it exists not to do.

          What was actually missing was any admission that the run was out of date. Marking
          a cost center non-allocable left "is allocable but no driver targets it" on
          screen, still correct about a configuration that no longer existed, and reading
          exactly like a bug.

          Warning colours rather than error: nothing is wrong, the answer is just old.
      */}
      {qcRun?.isStale && (
        <div
          className="flex items-center gap-3 border-b px-4 py-2 text-[12px]"
          style={{
            background: 'var(--color-warn-wash)',
            color: 'var(--color-warn)',
            borderColor: 'var(--color-rule-soft)',
          }}
          role="status"
        >
          <span className="flex-1">
            These findings are from {formatDateTime(qcRun.completedUtc ?? qcRun.startedUtc)}.
            The {qcRun.configurationChangedSource ?? 'configuration'} changed after that
            {qcRun.configurationChangedUtc
              ? `, at ${formatDateTime(qcRun.configurationChangedUtc)}`
              : ''}
            , so anything you have fixed since will still be listed until QC runs again.
          </span>

          <button
            className="btn btn-sm"
            disabled={runQc.isPending || effectivePeriodId === null}
            onClick={() => {
              setError(null)
              runQc.mutate(effectivePeriodId!, {
                onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
              })
            }}
          >
            {runQc.isPending ? 'Running…' : 'Run QC again'}
          </button>
        </div>
      )}

      {error && (
        <div
          className="border-b px-4 py-2 text-[12px]"
          style={{
            background: 'var(--color-error-wash)',
            color: 'var(--color-error)',
            borderColor: 'var(--color-rule-soft)',
          }}
          role="alert"
        >
          {error}
        </div>
      )}

      {/* Four cards. Errors first and largest — it is the headline, and the run button
          keys off exactly this number. */}
      <div
        className="flex shrink-0 gap-3 border-b px-4 py-3"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
      >
        <Card
          label="Errors"
          count={qcRun?.errorCount ?? 0}
          bg="var(--color-error-wash)"
          fg="var(--color-error)"
          active={severityFilter === 'Error'}
          onClick={() => setSeverityFilter(severityFilter === 'Error' ? null : 'Error')}
          hint="Blocking. A run cannot start while this is above zero, and no suppression can bring it down."
        />
        <Card
          label="Warnings"
          count={qcRun?.warningCount ?? 0}
          bg="var(--color-warn-wash)"
          fg="var(--color-warn)"
          active={severityFilter === 'Warning'}
          onClick={() => setSeverityFilter(severityFilter === 'Warning' ? null : 'Warning')}
          hint="Worth a look. Suppressible with a reason."
        />
        <Card
          label="Info"
          count={qcRun?.infoCount ?? 0}
          bg="var(--color-accent-wash)"
          fg="var(--color-accent)"
          active={severityFilter === 'Info'}
          onClick={() => setSeverityFilter(severityFilter === 'Info' ? null : 'Info')}
          hint="Informational."
        />
        <Card
          label="Suppressed"
          count={qcRun?.suppressedCount ?? 0}
          bg="var(--color-panel-alt)"
          fg="var(--color-muted)"
          active={showSuppressed}
          onClick={() => setSeverityFilter(showSuppressed ? null : 'Suppressed')}
          hint="Still generated and stored on every run, just filtered from the default view."
        />

        {qcRun?.blocksRun && (
          <div
            className="ml-auto flex items-center rounded px-3 text-[12px]"
            style={{ background: 'var(--color-error-wash)', color: 'var(--color-error)' }}
          >
            Runs are blocked until every error is resolved.
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto" style={{ background: 'var(--color-panel)' }}>
        {tab === 'coverage' ? (
          <CoverageTab periodId={effectivePeriodId} />
        ) : !qcRun ? (
          <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
            No QC run for this period yet. Press Run QC.
          </p>
        ) : grouped.length === 0 ? (
          <p className="p-4 text-[12px]" style={{ color: 'var(--color-ok)' }}>
            Nothing to report.
          </p>
        ) : (
          grouped.map(([ruleCode, items]) => (
            <RuleGroup
              key={ruleCode}
              ruleCode={ruleCode}
              description={ruleByCode.get(ruleCode)?.description}
              suppressible={ruleByCode.get(ruleCode)?.isSuppressible ?? false}
              findings={items}
            />
          ))
        )}
      </div>
    </div>
  )
}

function Card({
  label,
  count,
  bg,
  fg,
  active,
  onClick,
  hint,
}: {
  label: string
  count: number
  bg: string
  fg: string
  active: boolean
  onClick: () => void
  hint: string
}) {
  return (
    <button
      onClick={onClick}
      title={hint}
      className="flex min-w-28 flex-col items-start rounded px-3 py-2"
      style={{
        background: bg,
        color: fg,
        border: `1px solid ${active ? fg : 'transparent'}`,
      }}
    >
      <span className="num text-[20px] font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>
        {count}
      </span>
      <span className="col-head" style={{ color: 'inherit' }}>
        {label}
      </span>
    </button>
  )
}

function RuleGroup({
  ruleCode,
  description,
  suppressible,
  findings,
}: {
  ruleCode: string
  description?: string
  suppressible: boolean
  findings: QcFinding[]
}) {
  const [open, setOpen] = useState(true)
  const severity = findings[0]!.severity
  const style = SEVERITY[severity]

  return (
    <div className="border-b" style={{ borderColor: 'var(--color-rule-soft)' }}>
      <button
        className="flex w-full items-center gap-2 px-4 py-1.5 text-left"
        style={{ background: 'var(--color-panel-alt)' }}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden style={{ color: 'var(--color-faint)' }}>
          {open ? '▾' : '▸'}
        </span>
        <span className="badge" style={{ background: style.bg, color: style.fg }}>
          {ruleCode}
        </span>
        <span className="text-[12px]" style={{ color: 'var(--color-muted)' }}>
          {description}
        </span>
        <span className="num ml-auto text-[12px]" style={{ color: 'var(--color-muted)' }}>
          {findings.length}
        </span>
      </button>

      {open &&
        findings.map((finding) => (
          <FindingRow key={finding.qcFindingId} finding={finding} suppressible={suppressible} />
        ))}
    </div>
  )
}

function FindingRow({ finding, suppressible }: { finding: QcFinding; suppressible: boolean }) {
  const [suppressing, setSuppressing] = useState(false)
  const navigate = useNavigate()

  /** Every finding deep-links to the editor for the offending entity (§7.5). */
  function open() {
    switch (finding.entityType) {
      case 'CostDriver':
        navigate(`/drivers/${finding.entityId}`)
        break
      case 'CostCenter':
        navigate('/cost-centers')
        break
      case 'Account':
        navigate('/mapping')
        break
      default:
        break
    }
  }

  return (
    <div className="border-t px-4 py-1.5" style={{ borderColor: 'var(--color-rule-soft)' }}>
      <div className="flex items-start gap-2">
        <span className="ident shrink-0" style={{ color: 'var(--color-faint)', minWidth: 90 }}>
          {finding.entityLabel}
        </span>
        <span className="flex-1 text-[12px]">{finding.message}</span>

        {finding.isSuppressed ? (
          <span
            className="badge shrink-0"
            style={{ background: 'var(--color-panel-alt)', color: 'var(--color-muted)' }}
            title={`${finding.suppressionReason} — ${finding.suppressedBy}`}
          >
            suppressed
          </span>
        ) : (
          <span className="flex shrink-0 gap-2">
            {finding.entityId !== null && finding.entityType !== 'Period' && (
              <button
                className="text-[11px] underline"
                style={{ color: 'var(--color-accent)' }}
                onClick={open}
              >
                open
              </button>
            )}
            {suppressible && finding.entityId !== null && (
              <button
                className="text-[11px] underline"
                style={{ color: 'var(--color-muted)' }}
                onClick={() => setSuppressing(true)}
              >
                suppress
              </button>
            )}
          </span>
        )}
      </div>

      {finding.isSuppressed && finding.suppressionReason && (
        <div className="mt-0.5 pl-[98px] text-[11px]" style={{ color: 'var(--color-faint)' }}>
          {finding.suppressionReason} — {finding.suppressedBy}
          {finding.suppressionExpiresUtc &&
            `, expires ${new Date(finding.suppressionExpiresUtc).toLocaleDateString()}`}
        </div>
      )}

      {suppressing && <SuppressForm finding={finding} onDone={() => setSuppressing(false)} />}
    </div>
  )
}

/**
 * A suppression requires a reason before it will save.
 *
 * The reason is the audit trail six months later, not the suppression itself — which is
 * why it is mandatory here and mandatory in the database.
 */
function SuppressForm({ finding, onDone }: { finding: QcFinding; onDone: () => void }) {
  const [reason, setReason] = useState('')
  // §7.5: default to 12 months, editable. Expiry re-raises the finding, which is the point.
  const [expires, setExpires] = useState(() => {
    const d = new Date()
    d.setFullYear(d.getFullYear() + 1)
    return d.toISOString().slice(0, 10)
  })
  const [error, setError] = useState<string | null>(null)
  const suppress = useSuppressFinding()

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-[98px]">
      <input
        className="input w-80"
        placeholder="Why is this expected? (required)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        autoFocus
      />
      <input
        type="date"
        className="input input-mono w-36"
        value={expires}
        onChange={(e) => setExpires(e.target.value)}
      />
      <button
        className="btn btn-sm"
        disabled={reason.trim().length === 0 || suppress.isPending}
        title={reason.trim().length === 0 ? 'A reason is required.' : undefined}
        onClick={() =>
          suppress.mutate(
            {
              ruleCode: finding.ruleCode,
              entityType: finding.entityType,
              entityId: finding.entityId!,
              reason: reason.trim(),
              expiresUtc: expires ? new Date(expires).toISOString() : null,
            },
            {
              onSuccess: onDone,
              onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
            },
          )
        }
      >
        Suppress
      </button>
      <button className="btn btn-sm" onClick={onDone}>
        Cancel
      </button>
      {error && (
        <span className="text-[11px]" style={{ color: 'var(--color-error)' }}>
          {error}
        </span>
      )}
    </div>
  )
}

/**
 * The Cost Center Coverage tab.
 *
 * This is where §7.4's findings become actionable: the tree makes an overlooked sibling
 * obvious in a way a findings list does not, because you can see the node sitting empty
 * next to three that are not.
 */
function CoverageTab({ periodId }: { periodId: number | null }) {
  const { data: coverage = [] } = useCostCenterCoverage(periodId)

  if (coverage.length === 0) {
    return (
      <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
        No coverage data for this period.
      </p>
    )
  }

  return (
    <table className="w-full border-collapse">
      <thead className="sticky top-0 z-10" style={{ background: 'var(--color-panel-alt)' }}>
        <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
          <th className="col-head px-3 py-1.5">Cost center</th>
          <th className="col-head px-3 py-1.5">Code</th>
          <th className="col-head px-3 py-1.5 text-right">Drivers</th>
          <th className="col-head px-3 py-1.5 text-right">Received %</th>
          <th className="col-head px-3 py-1.5">Flags</th>
        </tr>
      </thead>
      <tbody>
        {coverage.map((row) => (
          <tr
            key={row.costCenterId}
            className="grid-row border-b"
            style={{
              borderColor: 'var(--color-rule-soft)',
              // A zero-coverage node is highlighted rather than merely listed — the
              // point of this tab is that it should be impossible to miss.
              background: row.hasNoCoverage ? 'var(--color-error-wash)' : undefined,
            }}
          >
            <td className="px-3 py-1">
              <span style={{ paddingLeft: `${row.depth * 16}px` }}>{row.name}</span>
            </td>
            <td className="ident px-3 py-1" style={{ color: 'var(--color-muted)' }}>
              {row.code}
            </td>
            <td
              className="num px-3 py-1"
              style={{ color: row.hasNoCoverage ? 'var(--color-error)' : 'var(--color-muted)' }}
            >
              {row.targetingDriverCount}
            </td>
            <td className="num px-3 py-1">
              {row.targetingDriverCount === 0 ? '—' : formatPercent(row.totalPercentAcrossDrivers)}
            </td>
            <td className="px-3 py-1">
              <span className="flex gap-1">
                {row.hasNoCoverage && (
                  <span
                    className="badge"
                    style={{ background: 'var(--color-error-wash)', color: 'var(--color-error)' }}
                    title="No driver targets this cost center. It will receive nothing, and every other check can still pass."
                  >
                    no coverage
                  </span>
                )}
                {row.createdSinceLastRun && row.lastCompletedRunId !== null && (
                  <span
                    className="badge"
                    style={{ background: 'var(--color-accent-wash)', color: 'var(--color-accent)' }}
                    title="Created since the last completed run (QC-C005)."
                  >
                    new
                  </span>
                )}
                {!row.isAllocable && (
                  <span
                    className="badge"
                    style={{ background: 'var(--color-panel-alt)', color: 'var(--color-muted)' }}
                  >
                    not allocable
                  </span>
                )}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
